import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ReportingDashboard = () => {
  const [user, setUser] = useState(null);
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState('');
  const navigate = useNavigate();

  // Form state
  const [formData, setFormData] = useState({
    week_ending: '',
    key_activities: '',
    impact_highlights: '',
    challenges: '',
    prayer_requests: ''
  });
  const [mediaFiles, setMediaFiles] = useState([]);
  const [uploadingMedia, setUploadingMedia] = useState(false);

  useEffect(() => {
    // Check if user is logged in
    const storedUser = localStorage.getItem('reporting_user');
    if (!storedUser) {
      navigate('/reporting/login');
      return;
    }

    const userData = JSON.parse(storedUser);
    setUser(userData);
    loadReports(userData.username, userData.role);
  }, [navigate]);

  const loadReports = async (username, role) => {
    try {
      let response;
      if (role === 'admin') {
        // Admin sees all reports from all countries
        response = await axios.get(`${BACKEND_URL}/api/reporting/reports/all`);
      } else {
        // Coordinators see only their own reports
        response = await axios.get(
          `${BACKEND_URL}/api/reporting/reports/my-reports?username=${username}`
        );
      }
      setReports(response.data.reports);
    } catch (error) {
      console.error('Error loading reports:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('reporting_user');
    navigate('/reporting/login');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setSuccess('');

    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/reporting/reports/submit?username=${user.username}`,
        formData
      );

      if (response.data.success) {
        const reportId = response.data.report_id;
        
        // Upload media if any files selected
        if (mediaFiles.length > 0) {
          setUploadingMedia(true);
          try {
            const mediaFormData = new FormData();
            mediaFormData.append('report_id', reportId);
            mediaFiles.forEach(file => {
              mediaFormData.append('files', file);
            });
            
            await axios.post(
              `${BACKEND_URL}/api/reporting/reports/upload-media`,
              mediaFormData,
              {
                headers: { 'Content-Type': 'multipart/form-data' }
              }
            );
            setSuccess('✅ Report and media submitted successfully!');
          } catch (error) {
            setSuccess('✅ Report submitted! Media upload failed: ' + error.message);
          } finally {
            setUploadingMedia(false);
          }
        } else {
          setSuccess('✅ Report submitted successfully!');
        }
        
        setShowForm(false);
        setMediaFiles([]);
        setFormData({
          week_ending: '',
          key_activities: '',
          impact_highlights: '',
          challenges: '',
          prayer_requests: ''
        });
        
        // Reload reports
        loadReports(user.username, user.role);
        
        // Clear success message after 3 seconds
        setTimeout(() => setSuccess(''), 3000);
      }
    } catch (error) {
      alert('Error submitting report: ' + (error.response?.data?.detail || error.message));
    } finally {
      setSubmitting(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  // Get current week ending (next Saturday)
  const getNextSaturday = () => {
    const today = new Date();
    const dayOfWeek = today.getDay();
    const daysUntilSaturday = (6 - dayOfWeek + 7) % 7 || 7;
    const saturday = new Date(today);
    saturday.setDate(today.getDate() + daysUntilSaturday);
    return saturday.toISOString().split('T')[0];
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-gray-600">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                📊 Reporting Dashboard
              </h1>
              <p className="text-sm text-gray-600 mt-1">
                {user?.role === 'admin' 
                  ? `Administrator - Viewing All Countries` 
                  : `${user?.country} Coordinator - ${user?.username}`
                }
              </p>
            </div>
            <div className="flex gap-3 items-center">
              {user?.role === 'admin' && (
                <button
                  onClick={() => navigate('/reporting/editorial')}
                  className="px-4 py-2 text-sm bg-purple-600 text-white rounded-lg hover:bg-purple-700"
                >
                  ✍️ Editorial Dashboard
                </button>
              )}
              <a
                href={`${BACKEND_URL}/api/reporting/manual/english`}
                download
                className="px-3 py-2 text-xs bg-blue-50 text-blue-700 rounded-lg hover:bg-blue-100 border border-blue-200"
                title="Download English Manual"
              >
                📄 Manual
              </a>
              <a
                href={`${BACKEND_URL}/api/reporting/manual/french`}
                download
                className="px-3 py-2 text-xs bg-blue-50 text-blue-700 rounded-lg hover:bg-blue-100 border border-blue-200"
                title="Télécharger le Manuel Français"
              >
                📄 Manuel
              </a>
              <button
                onClick={handleLogout}
                className="px-4 py-2 text-sm text-gray-700 hover:text-gray-900 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Logout
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Success Message */}
        {success && (
          <div className="mb-6 bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg">
            {success}
          </div>
        )}

        {/* Submit New Report Button */}
        {!showForm && (
          <div className="mb-6">
            <button
              onClick={() => setShowForm(true)}
              className="px-6 py-3 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 transition-colors"
            >
              ➕ Submit New Weekly Report
            </button>
          </div>
        )}

        {/* Report Form */}
        {showForm && (
          <div className="bg-white rounded-lg shadow-lg p-6 mb-8">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-bold text-gray-900">
                New Weekly Report
              </h2>
              <button
                onClick={() => setShowForm(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                ✕
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Week Ending Date
                </label>
                <input
                  type="date"
                  name="week_ending"
                  value={formData.week_ending}
                  onChange={handleChange}
                  max={getNextSaturday()}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  required
                />
                <p className="text-xs text-gray-500 mt-1">
                  Select the Saturday ending the week you're reporting on
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Key Activities & Programs This Week
                </label>
                <textarea
                  name="key_activities"
                  value={formData.key_activities}
                  onChange={handleChange}
                  rows="4"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  placeholder="Describe the main programs, activities, and events this week..."
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Community Impact Highlights
                </label>
                <textarea
                  name="impact_highlights"
                  value={formData.impact_highlights}
                  onChange={handleChange}
                  rows="4"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  placeholder="Share testimonies, listener feedback, stories of impact..."
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Challenges Faced
                </label>
                <textarea
                  name="challenges"
                  value={formData.challenges}
                  onChange={handleChange}
                  rows="3"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  placeholder="Technical issues, operational challenges, areas needing improvement..."
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Prayer Requests & Needs
                </label>
                <textarea
                  name="prayer_requests"
                  value={formData.prayer_requests}
                  onChange={handleChange}
                  rows="3"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  placeholder="Prayer needs, staff requests, community needs..."
                  required
                />
              </div>

              {/* Media Upload - Optional */}
              <div className="border-t border-gray-200 pt-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  📎 Attach Media Files (Optional)
                </label>
                <p className="text-xs text-gray-500 mb-3">
                  Upload images, videos, or audio files (max 10MB per file)
                </p>
                <input
                  type="file"
                  multiple
                  accept="image/*,video/*,audio/*"
                  onChange={(e) => setMediaFiles(Array.from(e.target.files))}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                />
                {mediaFiles.length > 0 && (
                  <div className="mt-2">
                    <p className="text-sm text-gray-600">
                      {mediaFiles.length} file(s) selected:
                    </p>
                    <ul className="text-xs text-gray-500 mt-1">
                      {mediaFiles.map((file, idx) => (
                        <li key={idx}>• {file.name} ({(file.size / 1024 / 1024).toFixed(2)} MB)</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>

              <div className="flex gap-4">
                <button
                  type="submit"
                  disabled={submitting || uploadingMedia}
                  className="flex-1 bg-green-600 text-white py-3 rounded-lg font-medium hover:bg-green-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
                >
                  {uploadingMedia ? 'Uploading Media...' : submitting ? 'Submitting...' : 'Submit Report'}
                </button>
                <button
                  type="button"
                  onClick={() => setShowForm(false)}
                  className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Reports List */}
        <div className="bg-white rounded-lg shadow">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">
              Your Submitted Reports ({reports.length})
            </h2>
          </div>

          {reports.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              <p>No reports submitted yet.</p>
              <p className="text-sm mt-2">Click "Submit New Weekly Report" to get started!</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-200">
              {reports.map((report, index) => (
                <div key={report.id} className="p-6 hover:bg-gray-50">
                  <div className="flex justify-between items-start mb-3">
                    <div>
                      {user?.role === 'admin' && (
                        <div className="mb-2">
                          <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs font-medium rounded">
                            {report.country}
                          </span>
                          <span className="ml-2 text-xs text-gray-500">
                            by {report.submitted_by}
                          </span>
                        </div>
                      )}
                      <h3 className="font-medium text-gray-900">
                        Week of {new Date(report.week_start).toLocaleDateString()} - {new Date(report.week_end).toLocaleDateString()}
                      </h3>
                      <p className="text-sm text-gray-500 mt-1">
                        Submitted: {new Date(report.submitted_at).toLocaleDateString()}
                      </p>
                    </div>
                    <span className="px-3 py-1 bg-green-100 text-green-800 text-xs font-medium rounded-full">
                      {report.status}
                    </span>
                  </div>
                  
                  <div className="space-y-2 text-sm">
                    <div>
                      <span className="font-medium text-gray-700">Activities:</span>
                      <p className="text-gray-600 mt-1 line-clamp-2">{report.programs_highlight}</p>
                    </div>
                    <div>
                      <span className="font-medium text-gray-700">Impact:</span>
                      <p className="text-gray-600 mt-1 line-clamp-2">{report.testimonies}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ReportingDashboard;
